import React, { useState, useMemo, useEffect } from 'react';
import { useBranches } from '../../hooks/useBranches';
import { FaEdit, FaTrash, FaPlus, FaFileExport, FaMapMarkerAlt } from 'react-icons/fa';
import { exportToCSV } from '../../utils/exportUtils';
import { confirmAlert } from 'react-confirm-alert';
import { toast } from 'react-toastify';
import PaginationControls from './PaginationControls';
import 'react-confirm-alert/src/react-confirm-alert.css';

const BranchList = () => {
  const { 
    loading, 
    openEditModal, 
    deleteBranch, 
    openCreateModal,
    filteredBranches,
    updateFilters,
    filters,
    allBranches,
    cities,
    states
  } = useBranches();
  
  const [currentPage, setCurrentPage] = useState(1);
  const [branchesPerPage] = useState(10);
  
  // Reset to first page when filters change
  useEffect(() => {
    setCurrentPage(1);
  }, [filteredBranches.length]);
  
  // Get current branches for pagination
  const { currentBranches, totalPages } = useMemo(() => {
    const indexOfLastBranch = currentPage * branchesPerPage;
    const indexOfFirstBranch = indexOfLastBranch - branchesPerPage;
    const current = filteredBranches.slice(indexOfFirstBranch, indexOfLastBranch);
    const total = Math.ceil(filteredBranches.length / branchesPerPage);
    return { currentBranches: current, totalPages: total };
  }, [filteredBranches, currentPage, branchesPerPage]);
  
  const handlePageChange = (pageNumber) => {
    setCurrentPage(pageNumber);
  };
  
  const handleSearchChange = (e) => {
    updateFilters({ search: e.target.value });
  };

  const handleCityChange = (e) => {
    updateFilters({ city: e.target.value });
  };

  const handleStateChange = (e) => {
    updateFilters({ state: e.target.value });
  };

  const handleExport = (dataToExport) => {
    try {
      // Export either all Branches or filtered ones based on parameter
      const data = dataToExport === 'all' ? allBranches : filteredBranches;
      const timestamp = new Date().toISOString().split('T')[0];
      const exportType = dataToExport === 'all' ? 'all' : 'filtered';
      const recordCount = data.length;
      
      // Export the data
      exportToCSV(data, `branches-export-${timestamp}`);
      
      // Show success toast
      toast.success(`Successfully exported ${recordCount} ${exportType} Branches`);
    } catch (error) {
      console.error('Export failed:', error);
      toast.error('Failed to export Branches');
    }
  };

  const handleDelete = (branchId) => {
    confirmAlert({
      title: 'Confirm Deletion',
      message: 'Are you sure you want to delete this branch?',
      buttons: [
        {
          label: 'Yes',
          onClick: () => deleteBranch(branchId)
        },
        {
          label: 'No',
          onClick: () => {}
        }
      ]
    });
  };

  // Helper function to get branch status badge color
  const getBranchStatusColor = (status) => {
    switch (status?.toLowerCase()) {
      case 'active':
        return 'text-green-800 bg-green-100';
      case 'inactive':
        return 'text-red-800 bg-red-100';
      default:
        return 'text-gray-800 bg-gray-100';
    }
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center h-40">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-blue-500"></div>
      </div>
    );
  }

  return (
    <div className="bg-white rounded-lg shadow overflow-hidden">
      <div className="p-4 border-b">
        <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center space-y-4 sm:space-y-0">
          <div>
            <h2 className="text-lg font-semibold">Branch Masters</h2>
            <p className="text-sm text-gray-500 mt-1">
              {filteredBranches?.length || 0} records shown (Total: {allBranches?.length || 0})
            </p>
          </div>
          <div className="flex flex-col sm:flex-row items-center space-y-2 sm:space-y-0 sm:space-x-4 w-full sm:w-auto">
            <div className="flex flex-wrap items-center gap-2 w-full sm:w-auto">
              {/* City Filter */}
              <div className="relative w-full sm:w-48">
                <select
                  className="w-full px-3 py-2 border border-gray-300 rounded-md pr-10"
                  value={filters.city}
                  onChange={handleCityChange}
                >
                  <option value="">All Cities</option>
                  {cities.map(city => (
                    <option key={city} value={city}>{city}</option>
                  ))}
                </select>
                {filters.city && (
                  <button 
                    onClick={() => updateFilters({ city: '' })}
                    className="absolute right-2 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600"
                  >
                    &times;
                  </button>
                )}
              </div>
              
              {/* State Filter */}
              <div className="relative w-full sm:w-48">
                <select
                  className="w-full px-3 py-2 border border-gray-300 rounded-md pr-10"
                  value={filters.state}
                  onChange={handleStateChange}
                >
                  <option value="">All States</option>
                  {states.map(state => (
                    <option key={state} value={state}>{state}</option>
                  ))}
                </select>
                {filters.state && (
                  <button 
                    onClick={() => updateFilters({ state: '' })}
                    className="absolute right-2 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600"
                  >
                    &times;
                  </button>
                )}
              </div>
              
              {/* Search Box */}
              <div className="relative w-full sm:w-64">
                <input
                  type="text"
                  className="w-full px-3 py-2 border border-gray-300 rounded-md pr-10"
                  placeholder="Search by name or address"
                  value={filters.search}
                  onChange={handleSearchChange}
                />
                {filters.search && (
                  <button 
                    onClick={() => updateFilters({ search: '' })}
                    className="absolute right-2 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600"
                  >
                    &times;
                  </button>
                )}
              </div>
            </div>
            <div className="flex space-x-2">
              <button
                onClick={openCreateModal}
                className="px-4 py-2 bg-green-100 text-green-600 hover:bg-green-200 rounded-md flex items-center transition-colors duration-200 border border-green-200 whitespace-nowrap"
              >
                <FaPlus className="mr-2" /> Add New
              </button>
              {/* <div className="relative inline-block text-left">
                <button
                  onClick={() => handleExport('all')}
                  className="px-4 py-2 bg-blue-100 text-blue-600 hover:bg-blue-200 rounded-md flex items-center transition-colors duration-200 border border-blue-200 whitespace-nowrap"
                >
                  <FaFileExport className="mr-2" /> Export All
                </button>
              </div>
              {filteredBranches.length !== allBranches.length && (
                <button
                  onClick={() => handleExport('filtered')}
                  className="px-4 py-2 bg-blue-100 text-blue-600 hover:bg-blue-200 rounded-md flex items-center transition-colors duration-200 border border-blue-200 whitespace-nowrap"
                >
                  <FaFileExport className="mr-2" /> Export Filtered
                </button>
              )} */}
            </div>
          </div>
        </div>
      </div>
      <table className="min-w-full divide-y divide-gray-200 table-fixed">
        <thead className="bg-gray-50">
          <tr>
            <th scope="col" className="w-3/12 px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider bg-gray-100">
              Branch Name ↓
            </th>
            <th scope="col" className="w-3/12 px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
              Address
            </th>
            <th scope="col" className="w-2/12 px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider bg-gray-100">
              City & State
            </th>
            <th scope="col" className="w-1/12 px-6 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">
              Branch ID
            </th>
            <th scope="col" className="w-1/12 px-6 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider bg-gray-100">
              Status
            </th>
            <th scope="col" className="w-2/12 px-6 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">
              Actions
            </th>
          </tr>
        </thead>
        <tbody className="divide-y divide-gray-200">
          {currentBranches && currentBranches.length > 0 ? currentBranches.map((branch) => (
            <tr 
              key={branch._id || branch.id} 
              className="hover:bg-blue-50 transition-colors duration-150"
            >
              <td className="px-6 py-4">
                <div className="text-sm font-medium text-gray-900 break-words">{branch.name}</div>
              </td>
              <td className="px-6 py-4">
                <div className="text-sm text-gray-900 break-words flex items-start">
                  <FaMapMarkerAlt className="text-red-500 mt-1 mr-1 flex-shrink-0" />
                  <span>{branch.address}</span>
                </div>
              </td>
              <td className="px-6 py-4">
                <div className="text-sm text-gray-900">
                  <div>{branch.city_name}</div>
                  <div className="text-gray-500">{branch.state}</div>
                </div>
              </td>
              <td className="px-6 py-4 text-center">
                <div className="text-sm break-words">{branch.phone || '-'}</div>
              </td>
              <td className="px-6 py-4 text-center">
                <span className={`inline-flex items-center justify-center px-2 py-1 text-xs font-bold leading-none rounded-full ${getBranchStatusColor(branch.branch_status)}`}>
                  {branch.branch_status || 'Unknown'}
                </span>
              </td>
              <td className="px-6 py-4 text-center">
                <div className="flex flex-wrap gap-2 justify-center">
                  <button
                    onClick={() => {
                      const branchId = branch._id || branch.id;
                      if (!branchId) {
                        console.error('Error: Missing branch ID for edit', branch);
                        return;
                      }
                      openEditModal(branch);
                    }}
                    className="px-3 py-1 bg-blue-50 text-blue-600 hover:bg-blue-100 rounded-md flex items-center transition-colors duration-200 border border-blue-200"
                  >
                    <FaEdit className="mr-1" /> Edit
                  </button>
                  <button
                    onClick={() => {
                      const branchId = branch._id || branch.id;
                      if (!branchId) {
                        console.error('Error: Missing branch ID for deletion', branch);
                        return;
                      }
                      handleDelete(branchId);
                    }}
                    className="px-3 py-1 bg-red-50 text-red-600 hover:bg-red-100 rounded-md flex items-center transition-colors duration-200 border border-red-200"
                  >
                    <FaTrash className="mr-1" /> Delete
                  </button>
                </div>
              </td>
            </tr>
          )) : (
            <tr>
              <td colSpan="6" className="px-6 py-10 text-center">
                <p className="text-gray-500">No Branches found. Try adjusting your search.</p>
                {filters.search && (
                  <button 
                    onClick={() => updateFilters({ search: '' })}
                    className="mt-2 px-4 py-1 bg-blue-50 text-blue-600 hover:bg-blue-100 rounded-md transition-colors duration-200 border border-blue-200"
                  >
                    Clear Search
                  </button>
                )}
              </td>
            </tr>
          )}
        </tbody>
      </table>
      
      {totalPages > 1 && (
        <div className="mt-4 px-6 py-3 border-t">
          <PaginationControls 
            currentPage={currentPage}
            totalPages={totalPages}
            onPageChange={handlePageChange}
          />
        </div>
      )}
    </div>
  );
};

export default BranchList;
